import { ThemeEvents } from '@theme/events';
import { DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { morph } from '@theme/morph';
import { isMobileBreakpoint } from '@theme/utilities';

class CartRecommendationsComponent extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {boolean} */
  #isLoading = false;

  /** @type {Map<string, Element>} */
  #quickAddCache = new Map();

  /** @type {AbortController | null} */
  #quickAddAbortController = null;

  /** @param {Event} event */
  #handleCartUpdate = (event) => {
    // Always load recommendations on cart update
    // This ensures recommendations load after cart section morphs
    this.#loadRecommendations('cart:update');
  };

  /** @param {Event} event */
  #handleDialogOpen = (event) => {
    // If we're inside a cart drawer, always load recommendations when drawer opens
    const isCartDrawer = this.closest('cart-drawer-component') !== null;
    
    if (isCartDrawer) {
      this.#loadRecommendations('dialog:open');
    }
  };

  connectedCallback() {
    // Check if drawer is already open when component connects
    // This handles the case where component is added via morphing after drawer opens
    const cartDrawer = this.closest('cart-drawer-component');
    const dialog = cartDrawer?.querySelector('dialog[ref="dialog"]');
    const isDrawerOpen = dialog?.open === true;
    
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    
    // Listen for custom load event from cart drawer
    this.addEventListener('load-recommendations', (event) => {
      const reason = event.detail?.reason || 'load-recommendations-event';
      this.#loadRecommendations(reason);
    });
    
    // Load recommendations on initial connect
    // If drawer is already open, load immediately (component was added after drawer opened)
    if (isDrawerOpen) {
      setTimeout(() => {
        this.#loadRecommendations('connectedCallback-drawerOpen');
      }, 0);
    } else {
      setTimeout(() => {
        this.#loadRecommendations('connectedCallback');
      }, 0);
    }
    
    // Mark component as ready for external triggers AFTER a microtask
    // This ensures all synchronous code in connectedCallback has run
    Promise.resolve().then(() => {
      this.dataset.ready = 'true';
    });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
  }

  /**
   * Public method to load recommendations (called by cart drawer)
   * @param {string} reason - Reason for loading
   */
  loadRecommendations(reason = 'unknown') {
    return this.#loadRecommendations(reason);
  }

  async #loadRecommendations(reason = 'unknown') {
    if (this.#isLoading) {
      this.#abortController?.abort();
    }

    this.#isLoading = true;

    let productId = this.dataset.productId;
    const baseUrl = this.dataset.url;
    const intent = this.dataset.intent || 'related';
    const limit = Number(this.dataset.limit || '4');
    const fallbackCollection = this.dataset.fallbackCollection;

    if (!productId) {
      productId = await this.#fetchLastCartProductId();
      if (productId) {
        this.dataset.productId = productId;
      }
    }

    this.#setLoading(true);

    if (!productId || !baseUrl) {
      const handled = await this.#loadFallbackCollection(fallbackCollection, limit);
      if (!handled) {
        this.#renderEmpty();
      }
      this.#isLoading = false;
      this.#setLoading(false);
      return;
    }

    const url = `${baseUrl}.json?product_id=${productId}&intent=${intent}&limit=${limit}`;

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#abortController.signal });
      
      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Could not read error response');
        throw new Error(`Recommendations request failed (${response.status}): ${errorText}`);
      }

      const data = await response.json();
      const products = Array.isArray(data.products) ? data.products : [];

      if (!products.length) {
        const handled = await this.#loadFallbackCollection(fallbackCollection, limit);
        if (!handled) {
          this.#renderEmpty();
        }
        this.#isLoading = false;
        return;
      }

      // Filter out hidden products before limiting to ensure we show the full limit
      const visibleProducts = products.filter((product) => !this.#hasHiddenTag(product));
      
      if (!visibleProducts.length) {
        const handled = await this.#loadFallbackCollection(fallbackCollection, limit);
        if (!handled) {
          this.#renderEmpty();
        }
        this.#isLoading = false;
        return;
      }

      this.#render(visibleProducts.slice(0, limit));
    } catch (error) {
      if (error.name !== 'AbortError') {
        const handled = await this.#loadFallbackCollection(fallbackCollection, limit);
        if (!handled) {
          this.#renderEmpty();
        }
      }
    } finally {
      this.#isLoading = false;
      this.#setLoading(false);
    }
  }

  /**
   * @param {any[]} products
   */
  #render(products) {
    const filteredProducts = products.filter((product) => !this.#hasHiddenTag(product));

    const list = document.createElement('div');
    list.className = 'cart-recommendations__items';
    list.setAttribute('role', 'list');

    for (const product of filteredProducts) {
      const card = this.#createCard(product);
      if (card) list.appendChild(card);
    }

    if (!list.childElementCount) {
      this.#renderEmpty();
      return;
    }

    this.replaceChildren(list);
  }

  #renderEmpty() {
    const message = this.dataset.emptyText || 'No recommendations available right now.';
    const emptyState = document.createElement('p');
    emptyState.className = 'cart-recommendations__empty';
    emptyState.textContent = message;

    this.replaceChildren(emptyState);
  }

  #hasHiddenTag(product) {
    if (!product?.tags) return false;

    if (Array.isArray(product.tags)) {
      return product.tags.some((tag) => tag?.toLowerCase() === 'hidden');
    }

    if (typeof product.tags === 'string') {
      return product.tags
        .split(',')
        .map((tag) => tag.trim().toLowerCase())
        .includes('hidden');
    }

    return false;
  }

  /**
   * @param {any} product
   */
  #createCard(product) {
    if (!product || !product.variants?.length || this.#hasHiddenTag(product)) return null;

    const card = document.createElement('article');
    card.className = 'cart-recommendations__item';
    card.setAttribute('role', 'listitem');

    const imageWrapper = document.createElement('a');
    imageWrapper.className = 'cart-recommendations__image-wrapper cart-recommendations__link';
    imageWrapper.href = product.url;
    imageWrapper.setAttribute('aria-label', product.title);

    const img = document.createElement('img');
    img.className = 'cart-recommendations__image';
    img.loading = 'lazy';

    const featuredImage = product.featured_image;
    if (typeof featuredImage === 'string' && featuredImage.length) {
      const url = new URL(featuredImage, window.location.origin);
      url.searchParams.set('width', '360');
      img.src = url.toString();
    } else if (featuredImage?.url) {
      img.src = `${featuredImage.url}?width=360`;
    }

    if (!img.src) {
      img.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 4 3"></svg>';
    }

    img.alt = product.title || 'Recommended product';

    imageWrapper.appendChild(img);

    const titleLink = document.createElement('a');
    titleLink.className = 'cart-recommendations__title-link';
    titleLink.href = product.url;
    titleLink.textContent = product.title;

    const price = document.createElement('span');
    price.className = 'cart-recommendations__price';
    const primaryPrice = product.price ?? product.variants?.[0]?.price ?? 0;
    price.textContent = this.#formatPrice(primaryPrice);

    const actions = document.createElement('div');
    actions.className = 'cart-recommendations__actions';
    const quickAddButton = this.#createQuickAddButton(product);
    if (quickAddButton) {
      actions.appendChild(quickAddButton);
    }

    card.append(imageWrapper, titleLink, price, actions);
    return card;
  }

  #setLoading(isLoading) {
    const placeholder = this.querySelector('.cart-recommendations__placeholder');
    if (!placeholder) return;

    placeholder.style.display = isLoading ? '' : 'none';
  }

  #formatPrice(cents) {
    try {
      if (window.Shopify?.formatMoney) {
        return Shopify.formatMoney(cents, window.Shopify.currency?.active_format || window.Shopify.money_format);
      }
    } catch (error) {
      console.error('Error formatting money:', error);
    }

    const value = Number(cents) / 100;
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD',
    }).format(value);
  }

  async #fetchLastCartProductId() {
    try {
      const response = await fetch('/cart.js');
      
      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Could not read error response');
        throw new Error(`Cart request failed (${response.status}): ${errorText}`);
      }

      const data = await response.json();
      const items = Array.isArray(data.items) ? data.items : [];
      const lastItem = items[items.length - 1];

      const productId = lastItem?.product_id ? String(lastItem.product_id) : '';
      return productId;
    } catch (error) {
      console.error('[CartRecommendations] Error fetching cart:', error);
      return '';
    }
  }

  async #loadFallbackCollection(handle, limit) {
    if (!handle) return false;

    try {
      const response = await fetch(`/collections/${encodeURIComponent(handle)}/products.json?limit=${limit}`);
      if (!response.ok) {
        throw new Error(`Fallback collection request failed (${response.status})`);
      }

      const data = await response.json();
      const products = Array.isArray(data.products) ? data.products : [];

      if (!products.length) return false;

      // Filter out hidden products before limiting
      const visibleProducts = products.filter((product) => !this.#hasHiddenTag(product));
      
      if (!visibleProducts.length) return false;

      this.#render(visibleProducts.slice(0, limit));
      return true;
    } catch (error) {
      console.error('Cart recommendations fallback error:', error);
      return false;
    }
  }

  #createQuickAddButton(product) {
    if (!product?.url) return null;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'button button--secondary cart-recommendations__quick-add';
    button.textContent = 'Add';
    button.addEventListener('click', (event) => this.#handleQuickAddClick(event, product));

    return button;
  }

  #buildProductUrl(product) {
    if (!product?.url) return '';

    const baseUrl = new URL(product.url, window.location.origin);
    const firstAvailableVariant = product.variants?.find((variant) => variant.available) || product.variants?.[0];

    if (firstAvailableVariant?.id) {
      baseUrl.searchParams.set('variant', firstAvailableVariant.id);
    }

    return baseUrl.toString();
  }

  async #handleQuickAddClick(event, product) {
    event.preventDefault();

    const button = event.currentTarget;
    if (!(button instanceof HTMLButtonElement)) return;

    button.disabled = true;
    button.classList.add('is-loading');

    try {
      const productUrl = this.#buildProductUrl(product);
      if (!productUrl) return;

      const content = await this.#getQuickAddContent(productUrl);
      if (!content) return;

      this.#prepareQuickAddContent(content, product, productUrl);
      this.#mountQuickAddContent(content);
      this.#openQuickAddModal();
    } catch (error) {
      console.error('Cart recommendations quick add error:', error);
    } finally {
      button.disabled = false;
      button.classList.remove('is-loading');
    }
  }

  async #getQuickAddContent(productUrl) {
    let cached = this.#quickAddCache.get(productUrl);

    if (!cached) {
      const html = await this.#fetchQuickAddContent(productUrl);
      const gridElement = html?.querySelector('[data-product-grid-content]');

      if (gridElement) {
        cached = /** @type {Element} */ (gridElement.cloneNode(true));
        this.#quickAddCache.set(productUrl, cached);
      }
    }

    return cached ? /** @type {Element} */ (cached.cloneNode(true)) : null;
  }

  async #fetchQuickAddContent(productUrl) {
    this.#quickAddAbortController?.abort();
    this.#quickAddAbortController = new AbortController();

    try {
      const response = await fetch(productUrl, {
        signal: this.#quickAddAbortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to load product page for quick add (${response.status})`);
      }

      const text = await response.text();
      return new DOMParser().parseFromString(text, 'text/html');
    } finally {
      this.#quickAddAbortController = null;
    }
  }

  #prepareQuickAddContent(content, product, productUrl) {
    if (isMobileBreakpoint()) {
      const productDetails = content.querySelector('.product-details');
      const productFormComponent = content.querySelector('product-form-component');
      const variantPicker = content.querySelector('variant-picker');
      const productPrice = content.querySelector('product-price');

      if (productDetails && productFormComponent && variantPicker && productPrice) {
        const productTitleLink = document.createElement('a');
        productTitleLink.textContent = product.title || '';
        productTitleLink.href = productUrl;
        productTitleLink.classList.add('view-product-title');

        const productHeader = document.createElement('div');
        productHeader.classList.add('product-header');
        productHeader.append(productTitleLink, productPrice);

        content.appendChild(productHeader);
        content.appendChild(variantPicker);
        content.appendChild(productFormComponent);
        productDetails.remove();
      }
    }
  }

  #mountQuickAddContent(content) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    morph(modalContent, content);
  }

  #openQuickAddModal() {
    const dialog = document.getElementById('quick-add-dialog');
    if (!dialog || typeof dialog.showDialog !== 'function') return;

    dialog.showDialog();
  }
}

if (!customElements.get('cart-recommendations-component')) {
  customElements.define('cart-recommendations-component', CartRecommendationsComponent);
}
